/**
 * BulkActionCommentModal
 *
 * Confirmation dialog used both by the queue's bulk accept / reject flow and
 * by a single drift's expanded detail. Built on the Carbon modal
 * (components/carbon/Modal): the action names the title, the count names the
 * subtitle, and the comment field carries the audit note. A reject is a
 * Carbon danger modal (Coral primary); every other status keeps the Soft
 * Green primary.
 *
 * Comment defaults to optional; pass `requireComment` to lock the confirm
 * button until the user gives a reason — used so rejections always carry a
 * note in the audit trail.
 */

import { useState, useEffect, useRef } from 'react'
import type { DriftStatus } from '@/types'
import { Modal } from '@/components/carbon/Modal'
import { TextArea } from '@/components/carbon/TextArea'

interface BulkActionCommentModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (comment: string) => void
  actionStatus: DriftStatus
  selectedCount: number
  isProcessing: boolean
  /** When true, an empty comment cannot be submitted (used for rejections so
   *  the audit trail always carries a reason). Defaults to false. */
  requireComment?: boolean
}

/** Verb shown in the title + the confirm button. */
const statusLabels: Record<string, string> = {
  Approved: 'Accept as new baseline',
  Rejected: 'Reject',
  New: 'Mark as new',
  Ignored: 'Ignore',
  AwaitingRemediation: 'Mark awaiting remediation',
  Remediated: 'Mark remediated',
}

const MAX_COMMENT = 500

export default function BulkActionCommentModal({
  isOpen,
  onClose,
  onConfirm,
  actionStatus,
  selectedCount,
  isProcessing,
  requireComment = false,
}: BulkActionCommentModalProps) {
  const [comment, setComment] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    if (isOpen) setComment('')
  }, [isOpen])

  const trimmed = comment.trim()
  const missingRequiredComment = requireComment && trimmed.length === 0

  const handleConfirm = () => {
    if (missingRequiredComment) return
    onConfirm(trimmed)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Submit on Ctrl+Enter / Cmd+Enter
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      handleConfirm()
    }
  }

  const label = statusLabels[actionStatus] ?? actionStatus
  const placeholder =
    actionStatus === 'Rejected'
      ? 'Tell the team why this change is being rejected.'
      : 'For example: accepted per change request CR-1234.'
  const driftWord = `drift${selectedCount === 1 ? '' : 's'}`
  const subtitle = requireComment
    ? `${selectedCount} selected ${driftWord}. Add a comment explaining why. It is recorded in the audit trail.`
    : `${selectedCount} selected ${driftWord}. Add an optional comment for the audit trail.`

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="sm"
      danger={actionStatus === 'Rejected'}
      title={label}
      subtitle={subtitle}
      initialFocus={textareaRef}
      primaryAction={{
        label: isProcessing ? 'Processing' : label,
        onClick: handleConfirm,
        disabled: missingRequiredComment,
        loading: isProcessing,
      }}
      secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: isProcessing }}
    >
      <TextArea
        id="bulk-action-comment"
        ref={textareaRef}
        label={`Comment (${requireComment ? 'required' : 'optional'})`}
        helper={`${comment.length}/${MAX_COMMENT} · Ctrl+Enter confirms`}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={isProcessing}
        rows={3}
        maxLength={MAX_COMMENT}
        placeholder={placeholder}
      />
    </Modal>
  )
}
